export type FuturesSymbolCategory =
  | "Equity Index"
  | "Rates"
  | "FX"
  | "Metals"
  | "Energy"
  | "Agriculture"
  | "Crypto";

// `quarterly_HMUZ` indices futures expire 3rd Friday of Mar/Jun/Sep/Dec.
// Brokers like FXPRO publish per-contract symbols (e.g. `US100M2026`), so
// the resolver appends the active contract suffix at lookup time.
export type RolloverCycle = "quarterly_HMUZ";

export interface FuturesSymbolMeta {
  symbol: string;
  label: string;
  category: FuturesSymbolCategory;
  tradingView: string;
  rolloverCycle?: RolloverCycle;
}

export const FUTURES_SYMBOLS: FuturesSymbolMeta[] = [
  // Equity indices: prefer broker-published CFDs (FXPRO futures, fallback
  // CAPITALCOM/FPMARKETS) so the free TradingView embed renders without a
  // CME data-subscription paywall. CME_MINI:* still works on the chart for
  // anyone with an entitled account but blanks out for everyone else.
  { symbol: "NQ", label: "E-mini Nasdaq 100", category: "Equity Index", tradingView: "FXPRO:US100", rolloverCycle: "quarterly_HMUZ" },
  { symbol: "MNQ", label: "Micro Nasdaq 100", category: "Equity Index", tradingView: "FXPRO:US100", rolloverCycle: "quarterly_HMUZ" },
  { symbol: "ES", label: "E-mini S&P 500", category: "Equity Index", tradingView: "FXPRO:US500", rolloverCycle: "quarterly_HMUZ" },
  { symbol: "MES", label: "Micro S&P 500", category: "Equity Index", tradingView: "FXPRO:US500", rolloverCycle: "quarterly_HMUZ" },
  // FXPRO has no Dow futures contract — fall back to FPMARKETS cash CFD.
  { symbol: "YM", label: "E-mini Dow", category: "Equity Index", tradingView: "FPMARKETS:US30" },
  { symbol: "MYM", label: "Micro Dow", category: "Equity Index", tradingView: "FPMARKETS:US30" },
  { symbol: "RTY", label: "E-mini Russell 2000", category: "Equity Index", tradingView: "FXPRO:US2000", rolloverCycle: "quarterly_HMUZ" },
  { symbol: "M2K", label: "Micro Russell 2000", category: "Equity Index", tradingView: "FXPRO:US2000", rolloverCycle: "quarterly_HMUZ" },
  // No retail-broker CFD for S&P MidCap — keep CME futures contract.
  { symbol: "EMD", label: "E-mini S&P MidCap 400", category: "Equity Index", tradingView: "CME_MINI:EMD1!" },
  { symbol: "NKD", label: "Nikkei 225 Dollar", category: "Equity Index", tradingView: "FXPRO:JP225", rolloverCycle: "quarterly_HMUZ" },
  { symbol: "VX", label: "VIX", category: "Equity Index", tradingView: "CAPITALCOM:VIX" },

  { symbol: "ZT", label: "2-Year T-Note", category: "Rates", tradingView: "CBOT:ZT1!" },
  { symbol: "ZF", label: "5-Year T-Note", category: "Rates", tradingView: "CBOT:ZF1!" },
  { symbol: "ZN", label: "10-Year T-Note", category: "Rates", tradingView: "CBOT:ZN1!" },
  { symbol: "TN", label: "Ultra 10-Year T-Note", category: "Rates", tradingView: "CBOT:TN1!" },
  { symbol: "ZB", label: "30-Year T-Bond", category: "Rates", tradingView: "CBOT:ZB1!" },
  { symbol: "UB", label: "Ultra T-Bond", category: "Rates", tradingView: "CBOT:UB1!" },

  { symbol: "6A", label: "Australian Dollar", category: "FX", tradingView: "CME:6A1!" },
  { symbol: "M6A", label: "Micro AUD/USD", category: "FX", tradingView: "CME_MINI:M6A1!" },
  { symbol: "6B", label: "British Pound", category: "FX", tradingView: "CME:6B1!" },
  { symbol: "M6B", label: "Micro GBP/USD", category: "FX", tradingView: "CME_MINI:M6B1!" },
  { symbol: "6C", label: "Canadian Dollar", category: "FX", tradingView: "CME:6C1!" },
  { symbol: "MCD", label: "Micro CAD/USD", category: "FX", tradingView: "CME_MINI:MCD1!" },
  { symbol: "6E", label: "Euro FX", category: "FX", tradingView: "CME:6E1!" },
  { symbol: "M6E", label: "Micro EUR/USD", category: "FX", tradingView: "CME_MINI:M6E1!" },
  { symbol: "6J", label: "Japanese Yen", category: "FX", tradingView: "CME:6J1!" },
  { symbol: "MJY", label: "Micro JPY/USD", category: "FX", tradingView: "CME_MINI:MJY1!" },
  { symbol: "6S", label: "Swiss Franc", category: "FX", tradingView: "CME:6S1!" },
  { symbol: "MSF", label: "Micro CHF/USD", category: "FX", tradingView: "CME_MINI:MSF1!" },
  { symbol: "6N", label: "New Zealand Dollar", category: "FX", tradingView: "CME:6N1!" },
  { symbol: "6M", label: "Mexican Peso", category: "FX", tradingView: "CME:6M1!" },

  { symbol: "GC", label: "Gold", category: "Metals", tradingView: "COMEX:GC1!" },
  { symbol: "MGC", label: "Micro Gold", category: "Metals", tradingView: "COMEX_MINI:MGC1!" },
  { symbol: "SI", label: "Silver", category: "Metals", tradingView: "COMEX:SI1!" },
  { symbol: "SIL", label: "Micro Silver", category: "Metals", tradingView: "COMEX_MINI:SIL1!" },
  { symbol: "HG", label: "Copper", category: "Metals", tradingView: "COMEX:HG1!" },
  { symbol: "MHG", label: "Micro Copper", category: "Metals", tradingView: "COMEX_MINI:MHG1!" },
  { symbol: "PL", label: "Platinum", category: "Metals", tradingView: "NYMEX:PL1!" },
  { symbol: "PA", label: "Palladium", category: "Metals", tradingView: "NYMEX:PA1!" },

  { symbol: "CL", label: "Crude Oil WTI", category: "Energy", tradingView: "NYMEX:CL1!" },
  { symbol: "MCL", label: "Micro Crude Oil", category: "Energy", tradingView: "NYMEX:MCL1!" },
  { symbol: "NG", label: "Natural Gas", category: "Energy", tradingView: "NYMEX:NG1!" },
  { symbol: "QG", label: "E-mini Natural Gas", category: "Energy", tradingView: "NYMEX_MINI:QG1!" },
  { symbol: "HO", label: "Heating Oil", category: "Energy", tradingView: "NYMEX:HO1!" },
  { symbol: "RB", label: "RBOB Gasoline", category: "Energy", tradingView: "NYMEX:RB1!" },
  { symbol: "BZ", label: "Brent Crude", category: "Energy", tradingView: "NYMEX:BZ1!" },

  { symbol: "ZC", label: "Corn", category: "Agriculture", tradingView: "CBOT:ZC1!" },
  { symbol: "XC", label: "Mini Corn", category: "Agriculture", tradingView: "CBOT_MINI:XC1!" },
  { symbol: "ZS", label: "Soybeans", category: "Agriculture", tradingView: "CBOT:ZS1!" },
  { symbol: "XK", label: "Mini Soybeans", category: "Agriculture", tradingView: "CBOT_MINI:XK1!" },
  { symbol: "ZW", label: "Wheat", category: "Agriculture", tradingView: "CBOT:ZW1!" },
  { symbol: "XW", label: "Mini Wheat", category: "Agriculture", tradingView: "CBOT_MINI:XW1!" },
  { symbol: "ZM", label: "Soybean Meal", category: "Agriculture", tradingView: "CBOT:ZM1!" },
  { symbol: "ZL", label: "Soybean Oil", category: "Agriculture", tradingView: "CBOT:ZL1!" },
  { symbol: "ZO", label: "Oats", category: "Agriculture", tradingView: "CBOT:ZO1!" },
  { symbol: "ZR", label: "Rough Rice", category: "Agriculture", tradingView: "CBOT:ZR1!" },
  { symbol: "KE", label: "KC Wheat", category: "Agriculture", tradingView: "CBOT:KE1!" },
  { symbol: "LE", label: "Live Cattle", category: "Agriculture", tradingView: "CME:LE1!" },
  { symbol: "GF", label: "Feeder Cattle", category: "Agriculture", tradingView: "CME:GF1!" },
  { symbol: "HE", label: "Lean Hogs", category: "Agriculture", tradingView: "CME:HE1!" },
  { symbol: "DC", label: "Class III Milk", category: "Agriculture", tradingView: "CME:DC1!" },
  { symbol: "KC", label: "Coffee", category: "Agriculture", tradingView: "ICEUS:KC1!" },
  { symbol: "CT", label: "Cotton", category: "Agriculture", tradingView: "ICEUS:CT1!" },
  { symbol: "SB", label: "Sugar No. 11", category: "Agriculture", tradingView: "ICEUS:SB1!" },
  { symbol: "CC", label: "Cocoa", category: "Agriculture", tradingView: "ICEUS:CC1!" },
  { symbol: "OJ", label: "Orange Juice", category: "Agriculture", tradingView: "ICEUS:OJ1!" },

  { symbol: "BTC", label: "Bitcoin", category: "Crypto", tradingView: "CME:BTC1!" },
  { symbol: "MBT", label: "Micro Bitcoin", category: "Crypto", tradingView: "CME:MBT1!" },
  { symbol: "ETH", label: "Ether", category: "Crypto", tradingView: "CME:ETH1!" },
  { symbol: "MET", label: "Micro Ether", category: "Crypto", tradingView: "CME:MET1!" },
];

export const FUTURES_ALLOWLIST = FUTURES_SYMBOLS.map((item) => item.symbol);

const BY_SYMBOL = new Map(FUTURES_SYMBOLS.map((item) => [item.symbol, item]));

export function futuresSymbolMeta(symbol: string): FuturesSymbolMeta | null {
  return BY_SYMBOL.get(symbol.toUpperCase().replace(/[^A-Z0-9]/g, "")) ?? null;
}

export function resolveTradingViewFuturesSymbol(symbol: string): string {
  const clean = symbol.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const meta = BY_SYMBOL.get(clean);
  if (!meta) return "FPMARKETS:US100";
  if (meta.rolloverCycle === "quarterly_HMUZ") {
    return meta.tradingView + activeQuarterlyContract();
  }
  return meta.tradingView;
}

// CME equity-index futures expire on the 3rd Friday of the contract
// month at 09:30 ET. We treat the calendar-day cutoff (no intraday
// handoff) as good enough for the chart embed — the difference is one
// session at most.
export function activeQuarterlyContract(now: Date = new Date()): string {
  const cycle: Array<{ month0: number; code: string }> = [
    { month0: 2, code: "H" }, // March
    { month0: 5, code: "M" }, // June
    { month0: 8, code: "U" }, // September
    { month0: 11, code: "Z" }, // December
  ];
  const year = now.getUTCFullYear();
  for (const { month0, code } of cycle) {
    if (now.getTime() < thirdFridayUtc(year, month0).getTime()) {
      return `${code}${year}`;
    }
  }
  return `H${year + 1}`;
}

function thirdFridayUtc(year: number, month0: number): Date {
  const first = new Date(Date.UTC(year, month0, 1));
  const offset = (5 - first.getUTCDay() + 7) % 7;
  return new Date(Date.UTC(year, month0, 1 + offset + 14));
}
